import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  GraduationCap, Play, CheckCircle, Clock, Award, 
  ChevronRight, BookOpen, Target, Users, TrendingUp, Star,
  MessageCircle, FileText, Lightbulb, Trophy, ArrowRight,
  BarChart, Zap, Shield, Package, Video, Wand2, Send,
  Bell, Settings, CreditCard, Briefcase, Globe, DollarSign,
  Sparkles, Gift, UserCheck, Clapperboard, Home, Rocket,
  Crown, Heart, Share2, Building, CheckCircle2, Info
} from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// POD Training Modules - Comprehensive Platform Guide
const POD_MODULES = [
  {
    id: 'welcome',
    title: 'Welcome to AI Horse-Powered POD',
    subtitle: 'Your Business Success Journey Starts Here',
    icon: Rocket,
    color: 'from-cyan-500 to-blue-600',
    graphic: '🚀',
    lessons: [
      { 
        id: 'welcome-1', 
        title: 'What is the POD?', 
        content: `
## Welcome to Your AI Horse-Powered Business Hub! 🎯

**POD** stands for three powerful capabilities at your fingertips:

### 📦 Projects On Demand
Get professional marketing, design, and business services delivered by our expert team.

### 📰 Publish On Demand  
Create and distribute content across the web with AI-powered tools.

### 📣 Promote On Demand
Reach your audience through directory listings, affiliate programs, and multi-channel marketing.

---

### Your $120,000 Co-Op Funds 💰

**Congratulations!** Every new account receives **$120,000 in Co-Op Funds** automatically deposited!

These credits cover:
- ✅ ALL Done-For-You (DFY) services
- ✅ ALL Done-With-You (DWY) expert sessions
- ✅ Premium marketing campaigns
- ✅ Professional content creation

You're not just getting software — you're getting a full **40+ department business support team**.
        `
      },
      { 
        id: 'welcome-2', 
        title: 'The Success Journey', 
        content: `
## Your Path to Success 🏆

Follow this proven journey to maximize your results:

### Step 1: Create Account ✅
You're here! You've already taken the first step.

### Step 2: Complete Onboarding
Fill out your Brand Breakdown to help us understand your business. This information powers all our AI tools and ensures everything we create is on-brand.

### Step 3: Get AI Certified
Review and acknowledge our AI policies to earn your **"Voted Best of Best"** certification badge. Display it proudly on your website!

### Step 4: Place Your First Order
Use your $120k Co-Op Funds to get professional services delivered. Start with what you need most!

### Step 5: Publish Content
Use your publishing credits to share content, run ads, and boost visibility.

### Step 6: Grow & Scale
As you see results, continue leveraging the POD to scale your business.

---

💡 **Pro Tip:** Successful clients log into their POD dashboard **first thing every day** to check orders, review progress, and stay on track!
        `
      },
      { 
        id: 'welcome-3', 
        title: 'Your Member Benefits', 
        content: `
## What You Get as a POD Member 🎁

### Core Benefits

| Benefit | What You Get |
|---------|-------------|
| **$120K Co-Op Funds** | Covers all DFY & DWY services |
| **Publishing Credits** | 200-3,000/month based on plan |
| **AI Certification** | "Voted Best of Best" badge |
| **40+ Departments** | Full business support team |
| **Gift Portal** | Free resort certificates to share |
| **Affiliate Program** | Earn commissions promoting others |

### Plan-Specific Features

**Partner Plan ($997/mo)**
- Full Workspace access with all 7 systems
- $120k Co-Op Funding
- 15% Media Management Fee
- AI Workforce with 8+ AI employees
- 30-day free trial to start

---

### Outcomes You Can Expect

✅ Professional marketing without hiring a team
✅ Consistent brand presence across all channels
✅ More leads and customers through multi-channel approach
✅ Time saved on tasks you used to struggle with
✅ Expert guidance whenever you need it
        `
      }
    ]
  },
  {
    id: 'onboarding',
    title: 'Getting Started: Onboarding',
    subtitle: 'Set Up Your Account for Success',
    icon: UserCheck,
    color: 'from-green-500 to-emerald-600',
    graphic: '✨',
    lessons: [
      { 
        id: 'onboard-1', 
        title: 'Complete Your Brand Breakdown', 
        content: `
## Your Brand Breakdown 📋

The Brand Breakdown is the foundation of everything we do for you. It helps our team and AI tools understand:

### What We Learn

1. **Your Business Identity**
   - Business name and industry
   - Products/services you offer
   - What makes you unique

2. **Your Target Audience**
   - Who are your ideal customers?
   - What problems do they have?
   - Where do they hang out online?

3. **Your Brand Voice**
   - Professional, friendly, casual, or authoritative?
   - Key phrases you use
   - Tone that resonates with your audience

4. **Your Goals**
   - What do you want to achieve?
   - Short-term wins vs long-term vision
   - Metrics that matter to you

---

### How to Complete It

1. Go to **Onboarding** from your profile dropdown
2. Fill out each section thoughtfully
3. Save your progress as you go
4. Submit when complete

⏱️ **Time Required:** 15-30 minutes

💡 **Tip:** The more detail you provide, the better our services and AI tools perform for you!
        `
      },
      { 
        id: 'onboard-2', 
        title: 'Getting AI Certified', 
        content: `
## Become "Voted Best of Best" Certified 🏆

### What is AI Certification?

AI Certification recognizes businesses that commit to responsible AI practices. When you earn your badge:

- 🏅 Display the "Voted Best of Best" badge on your website
- 📈 Rank higher in our directory
- ⭐ Build trust with customers
- 🎯 Access premium features

### How to Get Certified

**Step 1: Complete Onboarding**
- Finish your Brand Breakdown
- This helps us serve you better

**Step 2: Review AI Policies**
- Read our internal AI policy
- Understand how AI is used in your services

**Step 3: Acknowledge Policies**
- Accept terms for AI usage
- Commit to ethical practices

**Step 4: Download Your Badge**
- Get your certification badge
- Embed it on your website
- Show it in emails and marketing

---

### Your Certification Badge

Your badge includes:
- AI Horse-Powered certification mark
- Your business name
- Embed code for your website
- QR code for verification
        `
      }
    ]
  },
  {
    id: 'dashboard',
    title: 'Using Your Dashboard',
    subtitle: 'Navigate Your Command Center',
    icon: Home,
    color: 'from-blue-500 to-indigo-600',
    graphic: '🎛️',
    lessons: [
      { 
        id: 'dash-1', 
        title: 'Dashboard Overview', 
        content: `
## Your Dashboard: Command Central 🎯

When you log in, your dashboard shows everything at a glance:

### Quick Stats

| Stat | What It Shows |
|------|---------------|
| **Active Orders** | Services currently being worked on |
| **Completed** | Finished projects to review |
| **Co-Op Balance** | Your remaining credits |

### Quick Links

**AGI Workhorse™** → Task Me Anything
**Done For You** → Place orders with our team
**Done With You** → Book expert sessions
**Do It Yourself** → Use AI tools
**AI Knowledge Base** → Ask Me Anything

### Cinematic Video Ad System

Create Hollywood-quality video ads:
- Video Ads, Reels, TV, Radio, Billboards
- Click "Open Studio" to start creating

### Partner Resources

Access valuable tools:
- AI Besty Affiliate Link (Earn $60 per referral)
- Gift Portal (Free resort stays)
- $120K Grant Information

---

💡 **Daily Habit:** Check your dashboard every morning to:
1. Review any completed work
2. Check order status updates
3. See notifications
4. Plan your day's priorities
        `
      },
      { 
        id: 'dash-2', 
        title: 'Understanding Notifications', 
        content: `
## Stay Informed with Notifications 🔔

### Where to Find Them

Click the **bell icon** in the top navigation to see your notifications.

### Types of Notifications

**Order Updates**
- "Your order has been submitted"
- "Work in progress on [Project]"
- "Order completed - please review"

**System Alerts**
- New features available
- Important account updates
- Billing reminders

**Community**
- Someone liked your listing
- New message received
- Affiliate conversion recorded

### Managing Notifications

- **Mark as Read:** Click individual notifications
- **Mark All Read:** Clear all at once
- **Click to Navigate:** Notifications link to relevant pages

---

### Notification Best Practices

✅ Check notifications daily
✅ Act on "Order Completed" notifications promptly
✅ Don't let your inbox pile up
✅ Turn on browser notifications for real-time alerts
        `
      }
    ]
  },
  {
    id: 'workhorse',
    title: 'AGI Workhorse™',
    subtitle: 'Task Me Anything - AI-Powered Task Execution',
    icon: Sparkles,
    color: 'from-cyan-500 to-teal-600',
    graphic: '🐴',
    lessons: [
      { 
        id: 'workhorse-1', 
        title: 'What is AGI Workhorse™?', 
        content: `
## AGI Workhorse™: Task Me Anything 🐴

### Your AI-Powered Task Execution Platform

AGI Workhorse™ is your personal AI assistant that can execute tasks directly in your CRM. Think of it as having a tireless worker that can handle repetitive tasks instantly.

### What Can It Do?

- **Create & Manage Leads** - Add new leads, update info, bulk imports
- **Handle Contacts** - Manage your contact database
- **Send Messages** - Emails, SMS, conversations
- **Manage Opportunities** - Create deals, update pipelines
- **Schedule Appointments** - Book, reschedule, cancel
- **Trigger Workflows** - Add contacts to automations
- **Create Tasks & Notes** - Stay organized
- **Send Invoices** - Bill your clients
- **And Much More!**

---

### How to Access

1. Click **"AGI Workhorse™"** in your navigation
2. Browse the workhorse library by category
3. Select the task you want to perform
4. Fill out the simple wizard form
5. Submit and watch the magic happen!

💡 **Pro Tip:** AGI Workhorse™ connects to YOUR CRM, so all actions happen in your system!
        `
      },
      { 
        id: 'workhorse-2', 
        title: 'Using AI Workhorses', 
        content: `
## Executing Tasks with Workhorses ⚡

### The Workhorse Library

Our library includes 30+ specialized workhorses organized by category:

| Category | Tasks |
|----------|-------|
| **Leads** | Create, Update, Import, Add to Workflow |
| **Contacts** | Manage, Search, Tag |
| **Conversations** | Start, Reply, Send Messages |
| **Opportunities** | Create Deals, Update Stage |
| **Appointments** | Schedule, Reschedule, Cancel |
| **Workflows** | Add to Automation, Trigger |
| **Tasks** | Create, Assign, Complete |
| **Notes** | Add to Records |
| **Invoices** | Create, Send |
| **Emails** | Send Emails, Campaigns |
| **SMS** | Send Text Messages |

---

### Step-by-Step Usage

**Step 1: Select a Workhorse**
- Browse by category or search
- Click on the workhorse you need

**Step 2: Fill the Wizard Form**
- Each workhorse has a simple form
- Required fields are marked
- Helpful tooltips guide you

**Step 3: Submit the Task**
- Click "Execute Task"
- The AI processes your request
- Results appear instantly

---

### Important Notes

⚠️ **CRM Must Be Connected**
- Go to Security Settings to connect your CRM
- Look for the green "Connected" status in the sidebar

📝 **Save Drafts**
- Incomplete tasks can be saved as drafts
- Resume them later from your task history

📊 **Track History**
- View all your executed tasks
- See success/failure status
- Re-run tasks if needed
        `
      }
    ]
  },
  {
    id: 'dfy',
    title: 'Done For You Services',
    subtitle: 'Let Our Team Handle Everything',
    icon: Package,
    color: 'from-purple-500 to-violet-600',
    graphic: '📦',
    lessons: [
      { 
        id: 'dfy-1', 
        title: 'How DFY Works', 
        content: `
## Done For You: We Handle Everything 🎯

### What is DFY?

**Done For You** means our expert team does the work while you focus on running your business.

### How It Works

**Step 1: Browse Services**
- Click "Done For You" in navigation
- Browse 40+ departments
- Find what you need

**Step 2: Place Your Order**
- Select a service
- Provide any required details
- Submit using your Co-Op Funds

**Step 3: We Work On It**
- Our team gets to work immediately
- You'll see status updates in your orders
- Ask questions anytime through order messages

**Step 4: Review & Approve**
- We deliver the completed work
- You review and request any revisions
- Approve when satisfied

---

### Available Departments

| Category | Services |
|----------|----------|
| **Marketing** | Google Ads, Meta Ads, TikTok, SEO |
| **Creative** | Video Production, Graphic Design |
| **Web** | Website Design, Funnel Building |
| **Content** | Blog Writing, Social Media Posts |
| **Tech** | CRM Setup, AI Agent Development |
| **Admin** | Data Entry, Research, Analysis |

💰 **Remember:** Your $120k Co-Op Funds cover ALL DFY services!
        `
      },
      { 
        id: 'dfy-2', 
        title: 'Placing Your First Order', 
        content: `
## Placing a DFY Order 🛒

### Step-by-Step Guide

**1. Navigate to Done For You**
Click "Done For You" in the main navigation.

**2. Choose a Department**
Browse categories or use the search to find what you need.

**3. Select a Service**
Click on a service card to see details, pricing, and deliverables.

**4. Fill Out the Order Form**
- Describe what you need
- Add any reference materials
- Set your timeline preferences

**5. Submit Your Order**
- Review your order details
- Confirm Co-Op Fund usage
- Submit!

**6. Track Progress**
- Go to "My Orders" to see status
- Message the team with questions
- Receive notifications on updates

---

### Order Statuses Explained

| Status | Meaning |
|--------|---------|
| **Submitted** | Order received, awaiting assignment |
| **In Progress** | Team is actively working |
| **Review** | Ready for your feedback |
| **Revision** | Making your requested changes |
| **Completed** | Finished and approved |

💡 **Tip:** Be as detailed as possible in your order description — the more we know, the better we deliver!
        `
      }
    ]
  },
  {
    id: 'dwy',
    title: 'Done With You Sessions',
    subtitle: 'Expert Guidance When You Need It',
    icon: Video,
    color: 'from-pink-500 to-rose-600',
    graphic: '🎥',
    lessons: [
      { 
        id: 'dwy-1', 
        title: 'Book Expert Sessions', 
        content: `
## Done With You: Collaborate with Experts 👥

### What is DWY?

**Done With You** sessions are live video calls where our experts guide you through tasks, answer questions, and help you implement solutions.

### Session Types

| Duration | Price | Best For |
|----------|-------|----------|
| **15 min** | $30 | Quick questions |
| **30 min** | $50 | Focused guidance |
| **60 min** | $75 | Deep dive sessions |

### What You Can Get Help With

- 🔧 CRM setup and configuration
- 📊 Marketing strategy planning
- 🎨 Design feedback and direction
- 💻 Technical troubleshooting
- 📈 Analytics review
- 🤖 AI tool training

---

### How to Book

1. Click **"Done With You"** in navigation
2. Choose your preferred session length
3. Select an available time slot
4. Describe what you need help with
5. Confirm your booking

📅 **Sessions available:** Monday-Friday, 9am-5pm (your timezone)

💡 **Come Prepared:** Have your questions written down and relevant materials ready to maximize your session time!
        `
      }
    ]
  },
  {
    id: 'diy',
    title: 'Do It Yourself AI Tools',
    subtitle: '30+ AI-Powered Tools at Your Fingertips',
    icon: Wand2,
    color: 'from-amber-500 to-orange-600',
    graphic: '🪄',
    lessons: [
      { 
        id: 'diy-1', 
        title: 'Exploring the AI Tools Hub', 
        content: `
## Your AI Tools Arsenal 🛠️

### What's Available?

The DIY Tools Hub gives you access to **30+ AI-powered tools** organized by category:

### Tool Categories

**✍️ Content Creation**
- AI Copywriter (ads, emails, blogs)
- AI Image Generator
- Content Rewriter
- Blog Article Generator

**🎬 Cinematic Ad Toolkit**
- Video Script Generator
- Headlines & Hooks Creator
- CTA Generator
- Audience Builder

**📊 Lead Generation**
- AI Lead Scraper
- Smart Lead Qualifier
- Cold Email Builder
- Prospect Finder

**💼 Sales Enablement**
- Sales Script Generator
- Proposal Writer
- Conversion Copy Tester

**🏢 Business Admin**
- Contract Analyzer
- Legal Doc Generator
- Business Planner

**🎨 Brand & Creative**
- Brand Kit Builder
- App/Website Builder
- Logo Concept Generator

---

### How to Use Tools

1. Go to **"Do It Yourself"**
2. Browse categories or search
3. Click on a tool to open it
4. Fill in the inputs
5. Click "Generate"
6. Copy or save your results
        `
      },
      { 
        id: 'diy-2', 
        title: 'Saving Your Projects', 
        content: `
## Save & Manage Your Work 💾

### My Projects

Every tool output can be saved as a project. Access them anytime from the **"My Projects"** tab.

### Saving a Project

1. Generate content with any tool
2. Click **"Save Project"**
3. Give it a name
4. Add to a folder (optional)
5. Done!

### Managing Projects

**View:** Click any project to see full content
**Edit:** Update name, folder, or notes
**Delete:** Remove projects you no longer need
**Search:** Find projects by name or tool type
**Filter:** Organize by folder

---

### Best Practices

✅ Save important outputs immediately
✅ Use descriptive names ("Facebook Ad - Summer Sale")
✅ Organize into folders by campaign or client
✅ Delete old/unused projects regularly
✅ Star your favorites for quick access
        `
      }
    ]
  },
  {
    id: 'ranking',
    title: 'Ranking System & SEO',
    subtitle: 'Track and Improve Your Search Rankings',
    icon: TrendingUp,
    color: 'from-green-500 to-teal-600',
    graphic: '📈',
    lessons: [
      { 
        id: 'rank-1', 
        title: 'Understanding the Ranking System', 
        content: `
## Your SEO Command Center 🎯

### What is the Ranking System?

Track, analyze, and improve your search engine rankings with professional-grade tools.

### Key Features

**📊 SEO Module**
- Track keyword rankings
- Monitor position changes
- Get AI keyword suggestions
- Run site audits

**📍 GBP Ranker**
- Optimize your Google Business Profile
- 26-item optimization checklist
- Track local rankings
- Improve map visibility

**📢 Google Ads Manager**
- Plan campaigns
- AI ad copy generator
- Budget tracking
- Performance monitoring

**📝 Content Calendar**
- Plan blog content
- AI content briefs
- Schedule publications
- Track content performance

---

### Getting Started

1. Go to **"Ranking System"**
2. Add your website as a project
3. Add target keywords to track
4. Run your first site audit
5. Follow the optimization recommendations
        `
      },
      { 
        id: 'rank-2', 
        title: 'DFY SEO Projects', 
        content: `
## Professional SEO Services 🚀

### Done-For-You SEO

Want experts to handle your SEO? Our DFY projects provide:

### What's Included

**Monthly SEO Package**
- Keyword research & optimization
- On-page SEO improvements
- Technical SEO fixes
- Link building
- Monthly reports

**Google Business Profile**
- Full GBP optimization
- Post scheduling
- Review management
- Local citation building

### Pricing by Plan

| Your Plan | SEO Price | Ads Price |
|-----------|-----------|-----------|
| Concierge | $99/mo | $99/mo |
| Growth | $250/mo | $250/mo |
| Activation | $499/mo | $499/mo |

---

### Starting a Project

1. Go to "Ranking System"
2. Click "Add New Project"
3. Enter your website URL
4. Choose SEO or PPC
5. Start your 7-day free trial!
        `
      }
    ]
  },
  {
    id: 'publishing',
    title: 'Publishing Content',
    subtitle: 'Share Your Message with the World',
    icon: Send,
    color: 'from-indigo-500 to-blue-600',
    graphic: '📰',
    lessons: [
      { 
        id: 'pub-1', 
        title: 'How Publishing Works', 
        content: `
## Publish On Demand 📣

### Your Publishing Credits

Every plan includes monthly publishing credits:

| Plan | Credits/Month |
|------|--------------|
| Partner | 1,000 |
| Enterprise | Custom |

**Cost:** 25 credits per publication

### What You Can Publish

**📝 Blog Articles** - Long-form content
**💼 Job Listings** - Hire talent
**📢 Classifieds** - Buy/sell/trade
**🎉 Events** - Promote happenings
**💬 Discussions** - Community posts
**🎬 Videos** - Video content

---

### Publishing to Directory

Enable **"Promote to Directory"** when publishing to:
- Appear on the main directory homepage
- Get discovered by people browsing
- Increase visibility and engagement

💡 **Tip:** Content with images/videos performs much better!
        `
      },
      { 
        id: 'pub-2', 
        title: 'Managing Your Content', 
        content: `
## Content Management 📋

### My Published Content

Access all your published content from the Publishing page:

### Content Actions

**Edit** - Update title, description, content
**Promote** - Add/remove from directory
**View** - See how it appears live
**Delete** - Remove content

### Filtering Content

- **All** - Everything you've published
- **Promoted** - Content on the directory
- **By Type** - Filter by content type

---

### Best Practices

✅ Add eye-catching images to every post
✅ Use relevant tags for discoverability
✅ Promote your best content to directory
✅ Update old content to keep it fresh
✅ Monitor engagement (views, likes)
        `
      }
    ]
  },
  {
    id: 'cinematic',
    title: 'Cinematic Ads Studio',
    subtitle: 'Create Hollywood-Quality Video Ads',
    icon: Clapperboard,
    color: 'from-rose-500 to-pink-600',
    graphic: '🎬',
    lessons: [
      { 
        id: 'cine-1', 
        title: 'The Cinematic Ad System', 
        content: `
## Create Stunning Video Ads 🎬

### What is the Cinematic Ad System?

Create complete ad packages that include:
- 📹 Video creative
- 📝 Headlines & ad copy
- 🎯 Call-to-action
- 🔗 Landing page
- ⚡ Automations

### Ad Types You Can Create

| Type | Format | Best For |
|------|--------|----------|
| **Video Ads** | 15-60 sec | Social media |
| **Reels/Shorts** | 15-30 sec | TikTok, Instagram |
| **TV Commercials** | 30-60 sec | Broadcast |
| **Radio Ads** | 30-60 sec | Audio |
| **Billboards** | Static | Outdoor |

---

### The Flow

**One Video → Everywhere**

Create once, deploy to:
1. Best of Best Directory (home base)
2. Meta (Facebook/Instagram)
3. Google Ads
4. TikTok
5. Email Campaigns
6. TV & CTV
7. Radio
8. Digital Billboards
        `
      }
    ]
  },
  {
    id: 'affiliate',
    title: 'Affiliate Program',
    subtitle: 'Earn While You Promote',
    icon: DollarSign,
    color: 'from-emerald-500 to-green-600',
    graphic: '💰',
    lessons: [
      { 
        id: 'aff-1', 
        title: 'How Affiliates Earn', 
        content: `
## Earn as an Affiliate 💰

### Two Ways to Earn

**1. Promote Businesses**
- Find businesses in the Affiliate Marketplace
- Get your unique affiliate link
- Share the link with potential customers
- Earn commission on conversions

**2. AI Besty Program**
- Get your AI Besty affiliate link
- Recommend businesses for the $120K Grant
- Earn **$60 per qualified business**
- Help businesses get "Voted Best of Best"

---

### Your Affiliate Dashboard

Track everything at **/affiliate**:

| Metric | Description |
|--------|-------------|
| **Total Clicks** | Link clicks tracked |
| **Conversions** | Successful referrals |
| **Earnings** | Commission earned |
| **Affiliate ID** | Your unique ID |

### Getting Started

1. Go to **Affiliate Program** in navigation
2. Browse the Affiliate Marketplace
3. Click "Promote" on businesses you like
4. Share your unique links
5. Watch your earnings grow!
        `
      },
      { 
        id: 'aff-2', 
        title: 'Affiliate Training', 
        content: `
## Maximize Your Affiliate Success 🚀

### Where to Learn More

For complete affiliate training including:
- Text message scripts
- Email templates
- Social media posts
- Voicemail scripts
- Messenger scripts

Go to **Affiliate Training** in your profile dropdown!

---

### Quick Tips

✅ Be authentic - promote businesses you believe in
✅ Personalize your messages
✅ Focus on the value (not just the link)
✅ Follow up with interested contacts
✅ Track what works and do more of it

### Key Talking Points

- "Voted Best of Best" recognition
- "Up to $120,000 in Co-Op Grant Funding"
- "AI Horse-Powered marketing technology"
- "Free and takes less than a minute"
        `
      }
    ]
  },
  {
    id: 'community',
    title: 'Community Features',
    subtitle: 'Connect, Share, and Engage',
    icon: Users,
    color: 'from-violet-500 to-purple-600',
    graphic: '👥',
    lessons: [
      { 
        id: 'comm-1', 
        title: 'Community Overview', 
        content: `
## Your Community Hub 🌐

### Community Features

Access from the **Community** dropdown in navigation:

**💬 Messages**
Direct messaging with other members and businesses.

**📢 Mentions**
See when someone mentions you in content or comments.

**🔖 Bookmarks**
Save businesses and content for later.

**🏢 My Business Listing** (Business accounts)
Manage your public directory listing.

---

### Engaging with Content

**Like** - Show appreciation ❤️
**Share** - Spread the word 📤
**Bookmark** - Save for later 🔖
**Comment** - Join the conversation 💬

### Building Your Network

- Browse the directory for businesses
- Message business owners
- Leave reviews on listings
- Share helpful content
        `
      }
    ]
  },
  {
    id: 'directory',
    title: 'Business Directory',
    subtitle: 'Get Found by Customers',
    icon: Globe,
    color: 'from-cyan-500 to-teal-600',
    graphic: '🌍',
    lessons: [
      { 
        id: 'dir-1', 
        title: 'Your Business Listing', 
        content: `
## Get Found & Grow 📍

### Your Directory Listing

Every business account gets a public listing in our directory.

### What's on Your Listing

- **Business Name & Logo**
- **Contact Information**
- **Description & About Us**
- **Services/Products**
- **Photos & Videos**
- **Reviews & Ratings**
- **Published Content**
- **Affiliate Programs You Offer**

### Managing Your Listing

Go to **My Business Listing** (Community dropdown):

1. **Basic Info** - Name, category, contact
2. **Media** - Logo, photos, videos
3. **About Us** - Rich text description
4. **Affiliate Program** - Set up commissions

---

### Getting More Visibility

✅ Complete all listing fields
✅ Add a professional logo
✅ Upload photos and videos
✅ Get reviews from customers
✅ Publish content regularly
✅ Get AI Certified for badge
        `
      },
      { 
        id: 'dir-2', 
        title: 'Leads Dashboard', 
        content: `
## Manage Your Leads 📊

### Contact Form Leads

When someone fills out the contact form on your listing, they become a lead!

### Leads Dashboard

Access at **Leads Dashboard** (in profile dropdown):

**Stats at a Glance:**
- New leads count
- Contacted leads
- Total leads

**For Each Lead:**
- Contact name & info
- Message they sent
- Preferred contact time
- Status (new/contacted/qualified/converted/closed)

### Working Your Leads

1. Review new leads daily
2. Reach out promptly
3. Update status as you progress
4. Mark as converted when they buy

💡 **Tip:** Fast response times dramatically increase conversion rates!
        `
      }
    ]
  },
  {
    id: 'concierge',
    title: 'AI Concierge & Athena',
    subtitle: 'Your 24/7 AI Assistants',
    icon: Sparkles,
    color: 'from-amber-500 to-yellow-500',
    graphic: '🤖',
    lessons: [
      { 
        id: 'ai-1', 
        title: 'Using AI Concierge', 
        content: `
## Your AI Support Team 🤖

### AI Concierge

Click the **AI Concierge** button in the top navigation for:

- **AI Chat Assistant** - Ask anything about the platform
- **Product Tour** - Get a guided walkthrough

### What to Ask

The AI Concierge can help with:

✅ "How do I place a DFY order?"
✅ "What are publishing credits?"
✅ "How do I get AI certified?"
✅ "What's included in my plan?"
✅ "How do I track my orders?"
✅ "How does the affiliate program work?"

### Athena - Executive Assistant

Athena is your AI executive assistant for deeper support:

- Strategic guidance
- Business planning help
- Complex questions
- Industry insights

Access through **AI Knowledge Base**.

---

💡 **Tip:** Start conversations with specific questions for best results!
        `
      }
    ]
  },
  {
    id: 'crm',
    title: 'CRM & Security Settings',
    subtitle: 'Connect & Secure Your Business Systems',
    icon: Shield,
    color: 'from-slate-500 to-gray-600',
    graphic: '🔐',
    lessons: [
      { 
        id: 'crm-1', 
        title: 'Connecting Your CRM', 
        content: `
## Connecting Your CRM 🔗

### Why Connect Your CRM?

Connecting your CRM (GoHighLevel) enables:
- **AGI Workhorse™** to execute tasks in YOUR system
- **Lead syncing** from directory listings
- **Automated workflows** across platforms
- **Unified data** in one place

---

### How to Connect

1. Go to **Security Settings** from the menu
2. Click the **Integration** tab
3. Enter your:
   - **Location ID** (from your GHL account)
   - **Private API Key** (from Settings > API in GHL)
4. Click **Connect**

### Getting Your API Credentials

**In GoHighLevel:**
1. Go to Settings (gear icon)
2. Click "API"
3. Copy your Location ID
4. Generate or copy your Private API Key

⚠️ **Important:** Keep your API key secure - it gives access to your CRM data.

---

### Connection Status

Check your connection status:
- **Green ✓** = Connected
- **Red ✗** = Disconnected

The status appears in:
- Security Settings page
- AGI Workhorse™ sidebar
        `
      },
      { 
        id: 'crm-2', 
        title: 'Security Policies & Audit', 
        content: `
## Security Settings Overview 🛡️

### Integration Tab
- Connect/disconnect your CRM
- View connection status
- Rotate API keys when needed

### Agent Policy Tab
Configure how AGI Workhorses operate:

| Setting | What It Controls |
|---------|------------------|
| **Kill Switch** | Emergency stop all automation |
| **Confirm Destructive** | Require approval for deletes |
| **Confirm Bulk** | Approval for bulk operations |
| **Max Bulk Size** | Limit records per batch |
| **Contact Edit** | Permission to modify contacts |
| **Delete Permission** | Allow record deletion |

---

### Audit Log Tab
See everything that happens:
- Who made changes
- What was changed
- When it happened
- Export to CSV for records

### Advanced Tab (Admin Only)
- Global kill switch
- Master capability controls
- System-wide policies

💡 **Best Practice:** Review audit logs weekly to monitor activity!
        `
      }
    ]
  },
  {
    id: 'account',
    title: 'Account Management',
    subtitle: 'Settings, Billing & Profile',
    icon: Settings,
    color: 'from-gray-500 to-slate-600',
    graphic: '⚙️',
    lessons: [
      { 
        id: 'acc-1', 
        title: 'Profile Dropdown Overview', 
        content: `
## Your Account Menu 👤

### Accessing Your Profile

Click your name/avatar in the top right to see:

### Menu Items

**For Business Accounts:**
- Onboarding - Complete your setup
- Sales Training - Learn selling skills
- Affiliate Training - Master affiliate marketing
- Leads Dashboard - View contact form leads
- Team Members - Add VAs and employees
- My Orders - Track service orders
- Billing & Credits - Manage subscription
- Settings - Account preferences
- AI Knowledge - Ask Athena

**For Admin Accounts (Additional):**
- Admin Dashboard
- Manage Orders
- Appointments
- Team
- Services
- Service Requests
- Partner Inquiries

---

### Quick Actions

🔔 **Notifications** - Bell icon for updates
🤖 **AI Concierge** - Get help anytime
👤 **Profile** - Update your info
🚪 **Log Out** - Securely sign out
        `
      },
      { 
        id: 'acc-2', 
        title: 'Billing & Credits', 
        content: `
## Managing Your Subscription 💳

### Billing Page

Access at **Billing & Credits** in profile dropdown.

### What You'll See

**Current Plan**
- Plan name and price
- Billing cycle
- Features included

**Publishing Credits**
- Current balance
- Monthly allocation
- Cost per publish (25 credits)
- "Get More Credits" option

**Co-Op Balance**
- Your $120k credits
- Used credits
- Remaining credits

**Payment History**
- Past invoices
- Receipts
- Transaction history

---

### Upgrading Your Plan

Need more publishing credits? Upgrade for:
- More monthly credits
- Additional features
- Priority support
- Better DFY pricing
        `
      }
    ]
  },
  {
    id: 'gift',
    title: 'Gift Portal',
    subtitle: 'Send Amazing Incentives',
    icon: Gift,
    color: 'from-pink-500 to-rose-500',
    graphic: '🎁',
    lessons: [
      { 
        id: 'gift-1', 
        title: 'Sending Gift Incentives', 
        content: `
## Delight Your Customers 🎁

### What is the Gift Portal?

Send FREE resort stay incentives and Hotel Savings Cards to customers, prospects, and partners!

### Two Types of Gifts Available

**🏝️ Resort Stay Certificates**
- 4-6 Day luxury vacations
- Destinations across the US, Mexico, Canada, France, Italy, and more
- Las Vegas, Cancun, Paris, Orlando, and many others!
- Perfect for high-value customers and closing deals

**💳 Hotel Savings Cards**
- Digital savings cards worth **$100 - $500**
- Use at over **1 million hotels** worldwide
- Access to **8,000+ car rental** locations
- **No activation fee** for your recipients!
- Valid for **18 months** from issue date

---

### How to Send Resort Certificates

1. Go to **Gift Portal** (in profile dropdown)
2. Click the **"Resort Stays"** tab
3. Select a destination (Las Vegas, Cancun, Paris, etc.)
4. Click **"Generate Certificate"**
5. Save the PDF and email it to your recipient

Each click generates a unique certificate - unlimited use!

---

### How to Send Hotel Savings Cards

1. Go to **Gift Portal** (in profile dropdown)
2. Click the **"Hotel Savings Cards"** tab
3. Choose the card amount ($100, $200, $300, or $500)
4. Enter recipient's full name and email
5. Add a personalized message (optional)
6. Click **"Send Card"**

The recipient instantly receives an email with their digital savings card!

---

### What Recipients Get with Hotel Savings Cards

| Feature | Details |
|---------|---------|
| **Hotels & Resorts** | 1,000,000+ properties with top brands |
| **Car Rentals** | 8,000+ locations with comparative pricing |
| **Activities** | Thousands of excursions and experiences |
| **Activation Fee** | None - completely free for recipients! |
| **Validity** | 18 months from issue date |

---

### Best Use Cases

✅ **Thank new customers** - Welcome them with a memorable gift
✅ **Reward referrals** - Show appreciation for word-of-mouth
✅ **Incentivize reviews** - Encourage happy customers to share
✅ **Close deals** - "Sign today and get a complimentary resort stay!"
✅ **Employee rewards** - Recognize top performers
✅ **Celebrate milestones** - Mark anniversaries or achievements

💡 **Pro Tip:** Use Hotel Savings Cards for quick, instant-delivery gifts. Use Resort Certificates for bigger impact on high-value prospects!
        `
      }
    ]
  }
];

// Module Card Component
function ModuleCard({ module, onClick, progress }) {
  const Icon = module.icon;
  const completedLessons = module.lessons.filter(l => progress[l.id]).length;
  const totalLessons = module.lessons.length;
  const percentComplete = Math.round((completedLessons / totalLessons) * 100);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.02 }}
      onClick={onClick}
      className="glass rounded-2xl p-6 border border-white/10 hover:border-white/30 transition-all cursor-pointer group"
    >
      {/* Graphic Header */}
      <div className={`h-20 rounded-xl bg-gradient-to-br ${module.color} flex items-center justify-center mb-4 relative overflow-hidden`}>
        <span className="text-4xl">{module.graphic}</span>
        <div className="absolute inset-0 bg-black/20" />
      </div>
      
      <div className="flex items-start gap-3 mb-3">
        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${module.color} flex items-center justify-center flex-shrink-0`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-white group-hover:text-cyan-400 transition-colors">{module.title}</h3>
          <p className="text-sm text-gray-400 truncate">{module.subtitle}</p>
        </div>
      </div>
      
      {/* Progress */}
      <div className="mt-4">
        <div className="flex items-center justify-between mb-2 text-xs">
          <span className="text-gray-400">{completedLessons} of {totalLessons} lessons</span>
          <span className={cn(
            "font-medium",
            percentComplete === 100 ? "text-green-400" : "text-white"
          )}>{percentComplete}%</span>
        </div>
        <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
          <motion.div
            className={`h-full bg-gradient-to-r ${module.color}`}
            initial={{ width: 0 }}
            animate={{ width: `${percentComplete}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
      </div>
      
      {percentComplete === 100 && (
        <div className="mt-3 flex items-center gap-2 text-green-400 text-sm">
          <CheckCircle className="w-4 h-4" />
          <span>Completed!</span>
        </div>
      )}
    </motion.div>
  );
}

// Lesson Content Renderer
function LessonContent({ lesson, module, onComplete, onBack, isCompleted, isLastLesson, onNextModule, hasNextModule }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex-1 p-6 overflow-y-auto"
    >
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={onBack}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            <ChevronRight className="w-5 h-5 text-gray-400 rotate-180" />
          </button>
          <div className="flex-1">
            <p className="text-sm text-gray-400">{module.title}</p>
            <h2 className="text-2xl font-bold text-white">{lesson.title}</h2>
          </div>
          {isCompleted && (
            <div className="flex items-center gap-2 px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">
              <CheckCircle className="w-4 h-4" />
              Completed
            </div>
          )}
        </div>
        
        {/* Module Graphic */}
        <div className={`h-32 rounded-xl bg-gradient-to-br ${module.color} flex items-center justify-center mb-6 relative overflow-hidden`}>
          <span className="text-6xl">{module.graphic}</span>
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
        </div>
        
        {/* Content */}
        <div className="glass rounded-xl p-6 border border-white/10 mb-8">
          <div 
            className="prose prose-invert max-w-none text-gray-300 leading-relaxed"
            dangerouslySetInnerHTML={{ 
              __html: lesson.content
                .replace(/^## (.*$)/gm, '<h2 class="text-xl font-bold text-white mt-6 mb-3 first:mt-0">$1</h2>')
                .replace(/^### (.*$)/gm, '<h3 class="text-lg font-semibold text-white mt-4 mb-2">$1</h3>')
                .replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>')
                .replace(/^- (.*$)/gm, '<li class="ml-4 mb-1">• $1</li>')
                .replace(/^\d+\. (.*$)/gm, '<li class="ml-4 mb-1 list-decimal">$1</li>')
                .replace(/\|(.*)\|/g, (match) => {
                  const cells = match.split('|').filter(c => c.trim());
                  return `<tr>${cells.map(c => `<td class="border border-white/10 px-3 py-2">${c.trim()}</td>`).join('')}</tr>`;
                })
                .replace(/✅/g, '<span class="text-green-400">✅</span>')
                .replace(/💡/g, '<span>💡</span>')
                .replace(/💰/g, '<span>💰</span>')
                .replace(/🎯/g, '<span>🎯</span>')
                .replace(/🚀/g, '<span>🚀</span>')
                .replace(/🏆/g, '<span>🏆</span>')
                .replace(/📋/g, '<span>📋</span>')
                .replace(/---/g, '<hr class="my-6 border-white/10" />')
            }}
          />
        </div>
        
        {/* Actions */}
        <div className="flex items-center justify-between gap-4">
          <Button
            variant="outline"
            className="border-white/20"
            onClick={onBack}
          >
            Back to Module
          </Button>
          <div className="flex items-center gap-3">
            {!isCompleted && (
              <Button
                onClick={onComplete}
                className={cn(
                  "bg-gradient-to-r text-white",
                  module.color
                )}
              >
                Mark Complete
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
            {isCompleted && isLastLesson && hasNextModule && (
              <Button
                onClick={onNextModule}
                className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white"
              >
                Next Module
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
            {isCompleted && !isLastLesson && (
              <Button
                onClick={onComplete}
                className={cn(
                  "bg-gradient-to-r text-white",
                  module.color
                )}
              >
                Next Lesson
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
            {isCompleted && isLastLesson && !hasNextModule && (
              <div className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 rounded-lg">
                <Trophy className="w-5 h-5" />
                Training Complete!
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function TrainingPage() {
  const { user } = useAuth();
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [progress, setProgress] = useState({});
  const token = localStorage.getItem('token');

  // Load progress
  useEffect(() => {
    const loadProgress = async () => {
      try {
        const res = await fetch(`${API_URL}/api/training/progress`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setProgress(data.progress || {});
        }
      } catch (error) {
        const localProgress = localStorage.getItem('podTrainingProgress');
        if (localProgress) setProgress(JSON.parse(localProgress));
      }
    };
    loadProgress();
  }, [token]);

  const markComplete = async (lessonId) => {
    const newProgress = { ...progress, [lessonId]: true };
    setProgress(newProgress);
    localStorage.setItem('podTrainingProgress', JSON.stringify(newProgress));
    
    try {
      await fetch(`${API_URL}/api/training/progress`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ module_id: lessonId, completed: true })
      });
    } catch (error) {}
    
    toast.success('Lesson completed! 🎉');
    
    // Move to next lesson
    if (selectedModule) {
      const currentIndex = selectedModule.lessons.findIndex(l => l.id === lessonId);
      const nextLesson = selectedModule.lessons[currentIndex + 1];
      if (nextLesson) {
        setSelectedLesson(nextLesson);
      } else {
        setSelectedLesson(null);
        toast.success('Module completed! 🏆');
      }
    }
  };

  const getTotalProgress = () => {
    const totalLessons = POD_MODULES.reduce((sum, m) => sum + m.lessons.length, 0);
    const completed = Object.keys(progress).filter(k => progress[k]).length;
    return Math.round((completed / totalLessons) * 100);
  };

  // Helper to get next module
  const getNextModule = () => {
    if (!selectedModule) return null;
    const currentIndex = POD_MODULES.findIndex(m => m.id === selectedModule.id);
    return POD_MODULES[currentIndex + 1] || null;
  };

  // Handle moving to next module
  const goToNextModule = () => {
    const nextModule = getNextModule();
    if (nextModule) {
      setSelectedModule(nextModule);
      setSelectedLesson(nextModule.lessons[0]);
      toast.success(`Starting: ${nextModule.title}`);
    }
  };

  // Check if current lesson is the last in module
  const isLastLesson = () => {
    if (!selectedModule || !selectedLesson) return false;
    const lastLesson = selectedModule.lessons[selectedModule.lessons.length - 1];
    return selectedLesson.id === lastLesson.id;
  };

  // Lesson View
  if (selectedModule && selectedLesson) {
    return (
      <DashboardLayout>
        <LessonContent
          lesson={selectedLesson}
          module={selectedModule}
          onComplete={() => markComplete(selectedLesson.id)}
          onBack={() => setSelectedLesson(null)}
          isCompleted={progress[selectedLesson.id]}
          isLastLesson={isLastLesson()}
          hasNextModule={!!getNextModule()}
          onNextModule={goToNextModule}
        />
      </DashboardLayout>
    );
  }

  // Module Detail View
  if (selectedModule) {
    const completedLessons = selectedModule.lessons.filter(l => progress[l.id]).length;
    const percentComplete = Math.round((completedLessons / selectedModule.lessons.length) * 100);
    
    return (
      <DashboardLayout>
        <div className="p-6 pt-8" data-testid="training-module-detail">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center gap-4 mb-6">
              <button
                onClick={() => setSelectedModule(null)}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-gray-400 rotate-180" />
              </button>
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${selectedModule.color} flex items-center justify-center`}>
                <selectedModule.icon className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-white">{selectedModule.title}</h1>
                <p className="text-gray-400">{selectedModule.subtitle}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-white">{percentComplete}%</p>
                <p className="text-sm text-gray-400">Complete</p>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="h-2 bg-white/10 rounded-full mb-8 overflow-hidden">
              <motion.div
                className={`h-full bg-gradient-to-r ${selectedModule.color}`}
                initial={{ width: 0 }}
                animate={{ width: `${percentComplete}%` }}
              />
            </div>
            
            {/* Lessons List */}
            <div className="space-y-3">
              {selectedModule.lessons.map((lesson, index) => (
                <motion.button
                  key={lesson.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => setSelectedLesson(lesson)}
                  className={cn(
                    "w-full glass rounded-xl p-5 border transition-all text-left flex items-center gap-4",
                    progress[lesson.id]
                      ? "border-green-500/30 hover:border-green-500/50"
                      : "border-white/10 hover:border-white/30"
                  )}
                >
                  <div className={cn(
                    "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0",
                    progress[lesson.id] ? "bg-green-500/20" : `bg-gradient-to-br ${selectedModule.color}`
                  )}>
                    {progress[lesson.id] ? (
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    ) : (
                      <span className="text-white font-bold">{index + 1}</span>
                    )}
                  </div>
                  <div className="flex-1">
                    <p className={cn(
                      "font-medium",
                      progress[lesson.id] ? "text-green-400" : "text-white"
                    )}>
                      {lesson.title}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </motion.button>
              ))}
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Main Overview
  return (
    <DashboardLayout>
      <div className="p-6 pt-8" data-testid="training-page">
        {/* Welcome Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
              <GraduationCap className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Welcome to My Training</h1>
              <p className="text-gray-400">
                {user?.name ? `Hi ${user.name}! ` : ''}Learn everything about your AI Horse-Powered Business Hub
              </p>
            </div>
          </div>
        </motion.div>
        
        {/* Overall Progress */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass rounded-2xl p-6 border border-cyan-500/20 mb-8"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-white">Your Training Progress</h2>
              <p className="text-gray-400">Complete all modules to master the POD</p>
            </div>
            <div className="text-right">
              <p className="text-4xl font-bold text-cyan-400">{getTotalProgress()}%</p>
              <p className="text-sm text-gray-400">Overall Complete</p>
            </div>
          </div>
          <div className="h-3 bg-white/10 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-cyan-500 to-blue-600"
              initial={{ width: 0 }}
              animate={{ width: `${getTotalProgress()}%` }}
              transition={{ duration: 0.5, delay: 0.2 }}
            />
          </div>
          <div className="flex items-center justify-between mt-4 text-sm">
            <span className="text-gray-400">
              {Object.keys(progress).filter(k => progress[k]).length} lessons completed
            </span>
            <span className="text-cyan-400 font-medium">
              {POD_MODULES.reduce((sum, m) => sum + m.lessons.length, 0)} total lessons
            </span>
          </div>
        </motion.div>
        
        {/* Modules Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {POD_MODULES.map((module, index) => (
            <motion.div
              key={module.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <ModuleCard
                module={module}
                onClick={() => setSelectedModule(module)}
                progress={progress}
              />
            </motion.div>
          ))}
        </div>
        
        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-12 text-center glass rounded-2xl p-8 border border-white/10"
        >
          <Trophy className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">Ready to Take Action?</h3>
          <p className="text-gray-400 mb-6">
            Apply what you've learned and start growing your business with the POD!
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Button className="bg-gradient-to-r from-purple-500 to-violet-600">
              <Package className="w-4 h-4 mr-2" /> Place a DFY Order
            </Button>
            <Button variant="outline" className="border-cyan-500/30 text-cyan-400">
              <Wand2 className="w-4 h-4 mr-2" /> Try AI Tools
            </Button>
            <Button variant="outline" className="border-green-500/30 text-green-400">
              <DollarSign className="w-4 h-4 mr-2" /> Start Earning as Affiliate
            </Button>
          </div>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
